import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { CustomResponseEntity, PaginatedResponse, PaginationParams } from "../types/common";
import {
  CreateOrganizationDto,
  OrganizationDto,
  OrganizationDetailDto,
  UpdateOrganizationDto,
  UserDto,
  PrivilegeDto,
  IdTypeDto,
} from "../types/dtos";
import {
  adminOrganizationService,
  adminUserService,
  AdminOrganizationSearchParams,
} from "../services/admin.service";
import { roleService } from "../services/role.service";
import { privilegeService } from "../services/privilege.service";
import { idTypeService } from "../services/user.service";
import {
  RoleDto,
  CreateRoleDto,
  UpdateRoleDto,
  RolePrivilegeDto,
  CreateRolePrivilegeDto,
  UserRoleDto,
} from "../types/dtos/role-privilege.dto";
import { rolePrivilegeService } from "../services/role-privilege.service";
import { adminAssignmentService } from "../services/admin-assignment.service";
import { api } from "../client/axiosClient";
import { ENDPOINTS } from "../endpoints";
import { CreateAssignmentDto, AssignmentRow } from "../types/dtos/assignment.dto";

export const adminKeys = {
  all: ["admin"] as const,
  organizations: () => [...adminKeys.all, "organizations"] as const,
  organizationsPaginated: (params?: AdminOrganizationSearchParams) =>
    [...adminKeys.organizations(), "paginated", params] as const,
  organization: (id: string) => [...adminKeys.organizations(), id] as const,
  organizationDetails: (id: string) => [...adminKeys.organizations(), id, "details"] as const,
  users: () => [...adminKeys.all, "users"] as const,
  usersPaginated: (params?: AdminUserSearchParams) =>
    [...adminKeys.users(), "paginated", params] as const,
  user: (id: string) => [...adminKeys.users(), id] as const,
  roles: () => [...adminKeys.all, "roles"] as const,
  rolesByOrg: (orgId: string) => [...adminKeys.roles(), "byOrg", orgId] as const,
  role: (id: string) => [...adminKeys.roles(), id] as const,
};

export const useAdminOrganizations = (
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDto[]>>
) => {
  return useQuery({
    queryKey: adminKeys.organizations(),
    queryFn: () => adminOrganizationService.findAllForAdmin(params),
    ...options,
  });
};

export const useAdminOrganizationsPaginated = (
  params?: AdminOrganizationSearchParams,
  options?: UseQueryOptions<CustomResponseEntity<PaginatedResponse<OrganizationDto>>>
) => {
  return useQuery({
    queryKey: adminKeys.organizationsPaginated(params),
    queryFn: () => adminOrganizationService.findAllForAdminPaginated(params),
    staleTime: 30000,
    ...options,
  });
};

export const useAdminOrganization = (
  id: string,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDto>>
) => {
  return useQuery({
    queryKey: adminKeys.organization(id),
    queryFn: () => adminOrganizationService.findById(id),
    enabled: !!id,
    ...options,
  });
};

export const useAdminOrganizationDetails = (
  id: string,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDetailDto>>
) => {
  return useQuery({
    queryKey: adminKeys.organizationDetails(id),
    queryFn: () => adminOrganizationService.findDetailsById(id),
    enabled: !!id,
    ...options,
  });
};

export const useAdminCreateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, logoFile }: { data: CreateOrganizationDto; logoFile?: File | null }) =>
      adminOrganizationService.create(data, logoFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
    },
  });
};

export const useAdminUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      logoFile,
    }: {
      id: string;
      data: UpdateOrganizationDto;
      logoFile?: File | null;
    }) => adminOrganizationService.update(id, data, logoFile),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
      queryClient.invalidateQueries({ queryKey: adminKeys.organization(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminKeys.organizationDetails(variables.id) });
    },
  });
};

export const useAdminDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminOrganizationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
    },
  });
};

export const useAdminUsers = (
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<UserDto[]>>
) => {
  return useQuery({
    queryKey: adminKeys.users(),
    queryFn: async () => adminUserService.findAllForAdmin(params) as any,
    ...options,
  });
};

export const useAdminUser = (
  id: string,
  options?: UseQueryOptions<CustomResponseEntity<UserDto>>
) => {
  return useQuery({
    queryKey: adminKeys.user(id),
    queryFn: () => adminUserService.findById(id),
    ...options,
  });
};

export interface AdminUserSearchParams extends PaginationParams {
  search?: string;
}

export const useAdminUsersPaginated = (
  params?: AdminUserSearchParams,
  options?: UseQueryOptions<CustomResponseEntity<PaginatedResponse<UserDto>>>
) => {
  return useQuery({
    queryKey: adminKeys.usersPaginated(params),
    queryFn: async () => adminUserService.findAllForAdmin(params) as any,
    staleTime: 30000,
    ...options,
  });
};

export const useAdminCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UserDto) => adminUserService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
};

export const useAdminUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserDto }) => adminUserService.update(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      queryClient.invalidateQueries({ queryKey: adminKeys.user(variables.id) });
    },
  });
};

export const useAdminDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminUserService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
};

export const adminUserRoleKeys = {
  all: ["admin", "userRoles"] as const,
  byUser: (userId: string) => [...adminUserRoleKeys.all, "byUser", userId] as const,
};

export const useAdminUserRoleAssignments = (
  userId: string | null,
  options?: UseQueryOptions<CustomResponseEntity<UserRoleDto[]>>
) => {
  return useQuery({
    queryKey: adminUserRoleKeys.byUser(userId || ""),
    queryFn: () =>
      api.get<CustomResponseEntity<UserRoleDto[]>>(ENDPOINTS.admin.userRoles(userId || ""), {
        headers: { "X-Organization-Id": "" },
      }),
    enabled: !!userId,
    staleTime: 30000,
    ...options,
  });
};

export const adminIdTypeKeys = {
  all: ["admin", "idTypes"] as const,
  list: () => [...adminIdTypeKeys.all, "list"] as const,
};

export const useAdminIdTypes = (options?: UseQueryOptions<CustomResponseEntity<IdTypeDto[]>>) => {
  return useQuery({
    queryKey: adminIdTypeKeys.list(),
    queryFn: () => idTypeService.findAll(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useAdminRolesByOrg = (
  organizationId: string,
  options?: UseQueryOptions<CustomResponseEntity<RoleDto[]>>
) => {
  return useQuery({
    queryKey: adminKeys.rolesByOrg(organizationId),
    queryFn: () => roleService.findByOrganization(organizationId),
    enabled: !!organizationId,
    staleTime: 30000,
    ...options,
  });
};

export const useAdminCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleDto) => roleService.create(data),
    onSuccess: (_response, variables) => {
      if (variables.organizationId) {
        queryClient.invalidateQueries({ queryKey: adminKeys.rolesByOrg(variables.organizationId) });
      }
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
};

export const useAdminUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) => roleService.update(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      if (variables.data.organizationId) {
        queryClient.invalidateQueries({
          queryKey: adminKeys.rolesByOrg(variables.data.organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: adminKeys.organizationDetails(variables.data.organizationId),
        });
      }
      queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
    },
  });
};

export const useAdminDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, organizationId: _organizationId }: { id: string; organizationId?: string }) =>
      roleService.delete(id),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      if (variables.organizationId) {
        queryClient.invalidateQueries({
          queryKey: adminKeys.rolesByOrg(variables.organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: adminKeys.organizationDetails(variables.organizationId),
        });
      }
      queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as string[];
          return key.includes("rolePrivileges");
        },
      });
    },
  });
};

export const adminPrivilegeKeys = {
  all: ["admin", "privileges"] as const,
  list: () => [...adminPrivilegeKeys.all, "list"] as const,
};

export const useAdminPrivileges = (
  options?: UseQueryOptions<CustomResponseEntity<PrivilegeDto[]>>
) => {
  return useQuery({
    queryKey: adminPrivilegeKeys.list(),
    queryFn: () => privilegeService.findAll(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const adminRolePrivilegeKeys = {
  all: ["admin", "rolePrivileges"] as const,
  byRole: (roleId: string) => [...adminRolePrivilegeKeys.all, "byRole", roleId] as const,
};

export const useAdminRolePrivileges = (
  roleId: string,
  options?: UseQueryOptions<CustomResponseEntity<RolePrivilegeDto[]>>
) => {
  return useQuery({
    queryKey: adminRolePrivilegeKeys.byRole(roleId),
    queryFn: () =>
      api.get<CustomResponseEntity<RolePrivilegeDto[]>>(
        `${ENDPOINTS.rolePrivileges.base()}/${roleId}`,
        NO_ORG_CONTEXT
      ),
    enabled: !!roleId,
    staleTime: 30000,
    ...options,
  });
};

export const useAdminCreateRolePrivilege = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRolePrivilegeDto) => rolePrivilegeService.create(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminRolePrivilegeKeys.byRole(variables.roleId),
      });
    },
  });
};

const NO_ORG_CONTEXT = { headers: { "X-Organization-Id": "" } };

export interface TierChange {
  entity: string;
  level: "none" | "read" | "update" | "manage";
}

export const useAdminUpdateRoleTiers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, tiers }: { roleId: string; tiers: TierChange[] }) => {
      const url = ENDPOINTS.rolePrivileges.tiers(roleId);
      return api.put(
        url,
        { tiers },
        NO_ORG_CONTEXT
      );
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminRolePrivilegeKeys.byRole(variables.roleId),
      });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
    },
  });
};

export const useAdminBatchSaveRolePrivileges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ roleId, privilegeIds }: { roleId: string; privilegeIds: string[] }) => {
      const baseUrl = ENDPOINTS.rolePrivileges.base();

      let existingPrivileges: RolePrivilegeDto[] = [];
      try {
        const existingResponse = await api.get<CustomResponseEntity<RolePrivilegeDto[]>>(
          `${baseUrl}/${roleId}`,
          NO_ORG_CONTEXT
        );
        existingPrivileges = existingResponse?.data ?? [];
      } catch (error: unknown) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status !== 404) {
          throw error;
        }
      }

      const existingById = new Map(existingPrivileges.map((rp) => [rp.privilegeId, rp]));

      for (const existingRp of existingPrivileges) {
        if (!privilegeIds.includes(existingRp.privilegeId)) {
          await api.delete(`${baseUrl}/${existingRp.id}`, NO_ORG_CONTEXT);
        }
      }

      for (const privilegeId of privilegeIds) {
        const existing = existingById.get(privilegeId);
        if (existing && !existing.isActive) {
          await api.put(
            `${baseUrl}/${existing.id}`,
            { ...existing, isActive: true },
            NO_ORG_CONTEXT
          );
        } else if (!existing) {
          await api.post(baseUrl, { roleId, privilegeId, isActive: true }, NO_ORG_CONTEXT);
        }
      }
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminRolePrivilegeKeys.byRole(variables.roleId),
      });
    },
  });
};

// ===== ASSIGNMENT HOOKS =====

export const adminAssignmentKeys = {
  all: ["admin", "assignments"] as const,
  users: () => [...adminAssignmentKeys.all, "users"] as const,
  userSearch: (search: string) => [...adminAssignmentKeys.users(), "search", search] as const,
  allOrgs: () => [...adminAssignmentKeys.all, "allOrgs"] as const,
  orgDetails: (id: string) => [...adminAssignmentKeys.all, "orgDetails", id] as const,
};

export const useAdminUserSearch = (
  search: string,
  options?: UseQueryOptions<CustomResponseEntity<UserDto[]>>
) => {
  return useQuery({
    queryKey: adminAssignmentKeys.userSearch(search),
    queryFn: () => adminAssignmentService.searchUsers(search),
    enabled: search.length >= 2,
    staleTime: 15000,
    ...options,
  });
};

export const useAdminAllOrganizations = (
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDto[]>>
) => {
  return useQuery({
    queryKey: adminAssignmentKeys.allOrgs(),
    queryFn: () => adminAssignmentService.getAllOrganizations(),
    staleTime: 60000,
    ...options,
  });
};

export const useAdminOrganizationsSearchPaginated = (
  params: { page: number; size: number; search?: string },
  options?: UseQueryOptions<CustomResponseEntity<PaginatedResponse<OrganizationDto>>>
) => {
  return useQuery({
    queryKey: [...adminAssignmentKeys.allOrgs(), "paginated", params],
    queryFn: () => adminAssignmentService.getAllOrganizationsPaginated(params),
    staleTime: 30000,
    ...options,
  });
};
export const useAdminOrgDetailsForAssignments = (
  id: string,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDto>>
) => {
  return useQuery({
    queryKey: adminAssignmentKeys.orgDetails(id),
    queryFn: () => adminAssignmentService.getOrganizationDetails(id),
    enabled: !!id,
    staleTime: 30000,
    ...options,
  });
};

export const useAdminCreateAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssignmentDto) => adminAssignmentService.createAssignment(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
      queryClient.invalidateQueries({ queryKey: adminKeys.roles() });
      queryClient.invalidateQueries({ queryKey: adminUserRoleKeys.all });
      if (variables.organizationId) {
        queryClient.invalidateQueries({
          queryKey: adminKeys.organizationDetails(variables.organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: adminKeys.rolesByOrg(variables.organizationId),
        });
      }
    },
  });
};

export const useAdminDeleteAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
      userRoleAssignmentId,
    }: {
      organizationId: string;
      userId: string;
      userRoleAssignmentId: string;
    }) => adminAssignmentService.deleteAssignment(organizationId, userId, userRoleAssignmentId),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: adminAssignmentKeys.all });
      queryClient.invalidateQueries({ queryKey: adminKeys.organizations() });
      queryClient.invalidateQueries({ queryKey: adminUserRoleKeys.all });
      if (variables.userId) {
        queryClient.invalidateQueries({
          queryKey: adminUserRoleKeys.byUser(variables.userId),
        });
      }
      if (variables.organizationId) {
        queryClient.invalidateQueries({
          queryKey: adminKeys.organizationDetails(variables.organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: adminKeys.rolesByOrg(variables.organizationId),
        });
      }
    },
  });
};

/**
 * Fetches all organization assignments by fetching all orgs and then
 * requesting detail for each one. Produces a flat list of AssignmentRow
 * objects suitable for the central assignments table.
 *
 * PERFORMANCE NOTE: This hook performs N+1 API calls (one per organization)
 * and processes all data client-side. For deployments with a large number
 * of organizations or assignments, a dedicated server-side assignments
 * endpoint should be implemented instead. See AssignmentsTable.tsx for
 * additional pagination documentation.
 */
export const useAllOrgAssignments = () => {
  const { data: orgsData } = useAdminAllOrganizations();
  const organizations: OrganizationDto[] = (orgsData?.data ?? []) as OrganizationDto[];

  return useQuery({
    queryKey: [...adminAssignmentKeys.all, "allDetails", organizations.map((o) => o.id)],
    queryFn: async () => {
      const rows: AssignmentRow[] = [];
      const results = await Promise.allSettled(
        organizations.map((org) => adminAssignmentService.getOrganizationDetails(org.id))
      );

      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const orgDetail = result.value?.data as OrganizationDetailDto | undefined;
        if (!orgDetail) return;

        const orgUsers = orgDetail.organizationsUsers;
        if (!orgUsers) return;

        for (const ou of orgUsers) {
          if (!ou.user) continue;
          const roles = ou.roles ?? [];
          for (const role of roles) {
            rows.push({
              userRoleAssignmentId: role.userRoleId,
              userId: ou.user.id,
              userName: ou.user.name || "",
              userLastName: ou.user.lastName || "",
              userIdNumber: ou.user.idNumber || "",
              userEmail: ou.user.email || "",
              userIsActive: ou.user.isActive,
              organizationId: orgDetail.id,
              organizationName: orgDetail.name || "",
              roleId: role.roleId,
              roleName: role.roleName,
              roleIsActive: role.isActive ?? true,
            });
          }
        }
      });

      return rows.filter((row) => row.roleIsActive !== false);
    },
    enabled: organizations.length > 0,
    staleTime: 30000,
  });
};
